// Ecran « Stock » : ce que l'atelier a en magasin.
//
// LA QUANTITE NE SE SAISIT PAS DIRECTEMENT. Il n'y a pas de champ « quantite »
// modifiable dans le formulaire d'un article : on entre, on sort, on corrige,
// et chaque geste laisse une ligne dans l'historique. Un chiffre qu'on peut
// remplacer a la main est un chiffre dont plus personne ne sait d'ou il vient
// -- et l'ecart avec la realite du magasin devient inexplicable.
//
// C'est la meme raison qui fait qu'une intervention decremente toute seule :
// personne ne ressaisit le stock apres avoir change des plaquettes.
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import api from "../api";
import Layout from "../components/Layout";
import { SqueletteTableau } from "../components/Squelette";
import { formaterDate } from "../format";
import { useModules } from "../modules";
import ModaleConfirmation from "../components/ModaleConfirmation";
import { UNITES, libelleUnite } from "../unites";
import Notification from "../components/Notification";


const SENS = [
  { valeur: "entree", label: "Entrée (livraison, retour)" },
  { valeur: "sortie", label: "Sortie (consommation, casse)" },
  { valeur: "correction", label: "Correction d’inventaire" },
];

const NOUVEAU_VIDE = {
  reference: "",
  designation: "",
  marque: "",
  unite: "piece",
  quantite_initiale: "",
  seuil_alerte: "",
  emplacement: "",
  prix_unitaire_ht: "",
};

/** Extrait le message du serveur — c'est lui qui dit pourquoi il a refusé. */
function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

/** Retire les champs laissés vides : le serveur ne veut pas de "" pour un nombre. */
function nettoyer(brut) {
  const corps = {};
  Object.entries(brut).forEach(([cle, valeur]) => {
    if (valeur !== "" && valeur !== null && valeur !== undefined) corps[cle] = valeur;
  });
  return corps;
}

const nombre = (v) => (v === null || v === undefined ? "—" : String(v));

export default function Stock({ user, onLogout }) {
  const modules = useModules();

  const [articles, setArticles] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [message, setMessage] = useState("");
  const [recherche, setRecherche] = useState("");
  const [seulementAlertes, setSeulementAlertes] = useState(false);

  const [creationOuverte, setCreationOuverte] = useState(false);
  const [nouveau, setNouveau] = useState(NOUVEAU_VIDE);
  const [enCours, setEnCours] = useState(false);

  const [modalMouvement, setModalMouvement] = useState(null); // article visé
  const [mouvement, setMouvement] = useState({ sens: "entree", quantite: "", motif: "" });

  const [modalHistorique, setModalHistorique] = useState(null); // article visé
  const [historique, setHistorique] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get("/articles")
      .then(({ data }) => setArticles(data))
      .catch((err) => setErreur(messageErreur(err, "Impossible de charger le stock.")))
      .finally(() => setChargement(false));
  }, []);

  useEffect(charger, [charger]);

  const annoncer = (texte) => {
    setMessage(texte);
    setErreur("");
    setTimeout(() => setMessage(""), 6000);
  };

  const creer = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post("/articles", nettoyer(nouveau))
      .then(({ data }) => {
        setNouveau(NOUVEAU_VIDE);
        setCreationOuverte(false);
        annoncer(`« ${data.designation} » ajouté au stock.`);
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Création impossible.")))
      .finally(() => setEnCours(false));
  };

  const enregistrerMouvement = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post(`/articles/${modalMouvement.id}/mouvements`, nettoyer(mouvement))
      .then(() => {
        annoncer(`Stock de « ${modalMouvement.designation} » mis à jour.`);
        setModalMouvement(null);
        setMouvement({ sens: "entree", quantite: "", motif: "" });
        charger();
      })
      // Le serveur refuse une sortie plus grande que le stock, et son message
      // porte les deux chiffres. On l'affiche tel quel : « stock insuffisant »
      // seul n'aiderait pas à savoir de combien corriger.
      .catch((err) => setErreur(messageErreur(err, "Mouvement refusé.")))
      .finally(() => setEnCours(false));
  };

  const ouvrirHistorique = (article) => {
    setModalHistorique(article);
    setHistorique(null);
    api
      .get(`/articles/${article.id}/mouvements`)
      .then(({ data }) => setHistorique(data))
      .catch(() => setHistorique([]));
  };

  const supprimer = (article) => {
    setConfirmation({
      titre: "Retirer cet article du catalogue ?",
      sousTitre: `${article.designation}${article.reference ? ` — ${article.reference}` : ""}`,
      message:
        "Son historique de mouvements est supprimé avec lui : plus personne ne "
        + "pourra expliquer les quantités passées. Cette action est irréversible.",
      libelleConfirmer: "Retirer du catalogue",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/articles/${article.id}`);
        annoncer(`« ${article.designation} » retiré du catalogue.`);
        charger();
      },
    });
  };

  // Le module n'est pas souscrit : le serveur répondrait 403 sur chaque appel.
  // Ce n'est PAS la protection — c'est `exiger_module` — seulement une façon
  // de ne pas afficher un écran vide et incompréhensible.
  if (modules.charge && !modules.module_stock) return <Navigate to="/" replace />;

  const filtres = (articles || []).filter((a) => {
    if (seulementAlertes && !a.sous_seuil) return false;
    const q = recherche.trim().toLowerCase();
    if (!q) return true;
    return (
      a.reference.toLowerCase().includes(q) ||
      a.designation.toLowerCase().includes(q) ||
      (a.marque || "").toLowerCase().includes(q)
    );
  });

  const nbAlertes = (articles || []).filter((a) => a.sous_seuil).length;

  return (
    <Layout user={user} onLogout={onLogout}>
      {confirmation && (
        <ModaleConfirmation {...confirmation} onFermer={() => setConfirmation(null)} />
      )}
      <div className="entete-page">
        <h1>Stock</h1>
        <div className="actions-groupe">
          <button
            className="btn-accent"
            onClick={() => {
              setCreationOuverte((v) => !v);
              setErreur("");
            }}
          >
            {creationOuverte ? "Annuler" : "+ Ajouter un article"}
          </button>
        </div>
      </div>

      <Notification message={message} />
      {/* Une modale ouverte affiche deja l'erreur a l'endroit ou l'on vient de
          la provoquer. La repeter derriere le voile la fait lire deux fois. */}
      {erreur && !modalMouvement && <p className="erreur">{erreur}</p>}

      {creationOuverte && (
        <form className="formulaire" onSubmit={creer}>
          <div className="grille-form">
            <label className="champ">
              <span className="champ-label">Référence *</span>
              <input
                required
                value={nouveau.reference}
                onChange={(e) => setNouveau({ ...nouveau, reference: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Désignation *</span>
              <input
                required
                value={nouveau.designation}
                onChange={(e) => setNouveau({ ...nouveau, designation: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Marque</span>
              <input
                value={nouveau.marque}
                onChange={(e) => setNouveau({ ...nouveau, marque: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Unité</span>
              <select
                value={nouveau.unite}
                onChange={(e) => setNouveau({ ...nouveau, unite: e.target.value })}
              >
                {UNITES.map((u) => (
                  <option key={u.valeur} value={u.valeur}>
                    {u.singulier}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ">
              <span className="champ-label">Quantité en magasin</span>
              <input
                type="number"
                min="0"
                step="any"
                value={nouveau.quantite_initiale}
                onChange={(e) =>
                  setNouveau({ ...nouveau, quantite_initiale: e.target.value })
                }
              />
            </label>
            <label className="champ">
              <span className="champ-label">Seuil d’alerte</span>
              <input
                type="number"
                min="0"
                step="any"
                value={nouveau.seuil_alerte}
                onChange={(e) => setNouveau({ ...nouveau, seuil_alerte: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Emplacement</span>
              <input
                placeholder="Étagère B3"
                value={nouveau.emplacement}
                onChange={(e) => setNouveau({ ...nouveau, emplacement: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Prix unitaire HT (€)</span>
              <input
                type="number"
                min="0"
                step="any"
                value={nouveau.prix_unitaire_ht}
                onChange={(e) =>
                  setNouveau({ ...nouveau, prix_unitaire_ht: e.target.value })
                }
              />
            </label>
          </div>
          <p className="sous-info">
            La quantité saisie ici est enregistrée comme une entrée : elle
            apparaîtra dans l’historique. Ensuite, elle ne bouge plus qu’au
            travers d’un mouvement — jamais en la retapant.
          </p>
          <div className="actions-form">
            <button className="btn-accent" type="submit" disabled={enCours}>
              {enCours ? "Enregistrement…" : "Ajouter au stock"}
            </button>
          </div>
        </form>
      )}

      <div className="filtres">
        <input
          className="barre-recherche"
          placeholder="Rechercher une référence, une désignation, une marque…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <label className="champ-interrupteur">
          <input
            type="checkbox"
            checked={seulementAlertes}
            onChange={(e) => setSeulementAlertes(e.target.checked)}
          />
          <span>
            Sous le seuil {nbAlertes > 0 && <strong>({nbAlertes})</strong>}
          </span>
        </label>
      </div>

      {/* SqueletteTableau rend un <tbody> : il vit DANS le tableau, jamais a
          cote. L'en-tete reste donc affiche pendant le chargement -- ce qui
          tombe bien, il ne depend d'aucune donnee. */}
      {!chargement && filtres.length === 0 ? (
        <p className="vide">
          {articles && articles.length === 0
            ? "Aucun article en stock pour le moment."
            : "Aucun article ne correspond à cette recherche."}
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Désignation</th>
              <th>En stock</th>
              <th>Seuil</th>
              <th>Emplacement</th>
              <th>Actions</th>
            </tr>
          </thead>
          {chargement && <SqueletteTableau lignes={6} colonnes={6} />}
          {!chargement && (
          <tbody>
            {filtres.map((a) => (
              <tr key={a.id} className={a.sous_seuil ? "urgence-rappel" : ""}>
                <td>{a.reference}</td>
                <td>
                  {a.designation}
                  {a.marque && <span className="note-discrete"> · {a.marque}</span>}
                </td>
                <td>
                  <strong>{nombre(a.quantite)}</strong> {libelleUnite(a.unite, a.quantite)}{" "}
                  {a.sous_seuil && <span className="badge">à recommander</span>}
                </td>
                <td>{nombre(a.seuil_alerte)}</td>
                <td>{a.emplacement || "—"}</td>
                <td className="actions-cellule">
                  <button
                    className="lien-action"
                    onClick={() => {
                      setModalMouvement(a);
                      setMouvement({ sens: "entree", quantite: "", motif: "" });
                      setErreur("");
                    }}
                  >
                    Mouvement
                  </button>
                  <button className="lien-action" onClick={() => ouvrirHistorique(a)}>
                    Historique
                  </button>
                  {user.role === "admin" && (
                    <button
                      className="lien-action lien-danger"
                      onClick={() => supprimer(a)}
                    >
                      Retirer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          )}
        </table>
      )}

      {modalMouvement && (
        <div className="modal-overlay" onClick={() => setModalMouvement(null)}>
          <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-titre">Mouvement de stock</h2>
            <p className="modal-sous-titre">
              {modalMouvement.designation} — {nombre(modalMouvement.quantite)}{" "}
              {libelleUnite(modalMouvement.unite, modalMouvement.quantite)} en magasin
            </p>
            <form onSubmit={enregistrerMouvement}>
              <label className="modal-champ">
                <span className="champ-label">Sens</span>
                <select
                  value={mouvement.sens}
                  onChange={(e) => setMouvement({ ...mouvement, sens: e.target.value })}
                >
                  {SENS.map((s) => (
                    <option key={s.valeur} value={s.valeur}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="modal-champ">
                <span className="champ-label">Quantité *</span>
                <input
                  required
                  type="number"
                  min="0"
                  step="any"
                  value={mouvement.quantite}
                  onChange={(e) =>
                    setMouvement({ ...mouvement, quantite: e.target.value })
                  }
                />
              </label>
              <label className="modal-champ">
                <span className="champ-label">Motif</span>
                <input
                  placeholder="Livraison fournisseur, casse, inventaire…"
                  value={mouvement.motif}
                  onChange={(e) => setMouvement({ ...mouvement, motif: e.target.value })}
                />
              </label>
              {erreur && <p className="modal-erreur">{erreur}</p>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setModalMouvement(null)}
                >
                  Annuler
                </button>
                <button className="btn-accent" type="submit" disabled={enCours}>
                  {enCours ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalHistorique && (
        <div className="modal-overlay" onClick={() => setModalHistorique(null)}>
          <div
            className="modal-carte modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-titre">Historique — {modalHistorique.designation}</h2>
            <p className="modal-sous-titre">
              Chaque ligne explique une variation. Le total doit retomber sur la
              quantité affichée : c’est ce qui rend le chiffre croyable.
            </p>
            {historique !== null && historique.length === 0 ? (
              <p className="vide">Aucun mouvement.</p>
            ) : (
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Sens</th>
                    <th>Quantité</th>
                    <th>Motif</th>
                  </tr>
                </thead>
                {historique === null && <SqueletteTableau lignes={4} colonnes={4} />}
                {historique !== null && (
                <tbody>
                  {historique.map((m) => (
                    <tr key={m.id}>
                      <td>{formaterDate(m.date_mouvement)}</td>
                      <td>{SENS.find((s) => s.valeur === m.sens)?.label || m.sens}</td>
                      <td>
                        {m.sens === "sortie" ? "−" : "+"}
                        {nombre(m.quantite)}
                      </td>
                      <td>
                        {m.motif || "—"}
                        {m.operation_id && (
                          <span className="note-discrete"> · intervention</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                )}
              </table>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setModalHistorique(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
